import React from "react";
import { Button, Col, Form, FormGroup, Row, Spinner } from "react-bootstrap";

import { updateBagObjectAppData } from "../../../../../api/RealEstate";

export default function BagObjectAppDataForm(props: {
  bagObject: IBagObject;
}): React.JSX.Element {
  /**
   * A form to update the BAG object's app data.
   */
  const [bagObject, setBagObject] = React.useState<IBagObject>(props.bagObject);

  const [appDataNotes, setAppDataNotes] = React.useState<string>(
    bagObject.appData?.notes || "",
  );
  const [appDataClearHeight, setAppDataClearHeight] = React.useState<
    number | null
  >(bagObject.appData?.clearHeight || null);
  const [appDataOfficeArea, setAppDataOfficeArea] = React.useState<
    number | null
  >(bagObject.appData?.officeArea || null);
  const [appDataMaxFloorLoad, setAppDataMaxFloorLoad] = React.useState<
    number | null
  >(bagObject.appData?.maxFloorLoad || null);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [successMessage, setSuccessMessage] = React.useState("");
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const isFormComplete = (): boolean => {
    /**
     * Check that at least one field value has changed.
     */
    return (
      appDataNotes !== (bagObject.appData?.notes || "") ||
      appDataClearHeight !== (bagObject.appData?.clearHeight || null) ||
      appDataOfficeArea !== (bagObject.appData?.officeArea || null) ||
      appDataMaxFloorLoad !== (bagObject.appData?.maxFloorLoad || null)
    );
  };

  const handleSubmitAppData = async (e: React.FormEvent) => {
    /**
     * Handle the form submission to update the BAG object's app data.
     */
    e.preventDefault();
    setIsLoading(true);
    // Clear any previous messages.
    setErrorMessage("");
    setSuccessMessage("");
    const bagObjectAppData: IBagObjectAppData = {
      notes: appDataNotes,
      clearHeight: appDataClearHeight,
      officeArea: appDataOfficeArea,
      maxFloorLoad: appDataMaxFloorLoad,
    };
    await updateBagObjectAppData(bagObject.id, bagObjectAppData)
      .then(() => {
        setBagObject({ ...bagObject, appData: bagObjectAppData });
        setSuccessMessage("Aanpassingen zijn succesvol opgeslagen.");
      })
      .catch((error: IApiError) => setErrorMessage(error.message));
    setIsLoading(false);
  };

  return (
    <Form
      className="d-flex flex-column flex-grow-1 justify-content-between align-items-center mt-3"
      onSubmit={handleSubmitAppData}
    >
      <div className="w-100">
        <FormGroup className="mb-3">
          <Form.Label htmlFor="notes">
            <span className="mb-0">
              <strong>Notities</strong>
            </span>
          </Form.Label>
          <Form.Control
            size="sm"
            className="rounded-0"
            type="text"
            as="textarea"
            rows={2}
            value={appDataNotes}
            onChange={(e) => {
              setAppDataNotes(e.target.value);
              setSuccessMessage("");
            }}
            required={false}
            id="notes"
          />
        </FormGroup>

        <Row className="mb-3">
          <Col md={6}>
            <FormGroup>
              <Form.Label htmlFor="clear_height">
                <strong>Vrije hoogte (m)</strong>
              </Form.Label>
              <Form.Control
                size="sm"
                type="number"
                step="0.01"
                value={appDataClearHeight !== null ? appDataClearHeight : ""}
                onChange={(e) => {
                  setAppDataClearHeight(
                    e.target.value ? parseFloat(e.target.value) : null,
                  );
                  setSuccessMessage("");
                }}
                id="clear_height"
              />
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup>
              <Form.Label htmlFor="max_floor_load">
                <strong>Maximale vloerbelasting (kg/m²)</strong>
              </Form.Label>
              <Form.Control
                size="sm"
                type="number"
                step="0.01"
                value={appDataMaxFloorLoad !== null ? appDataMaxFloorLoad : ""}
                onChange={(e) => {
                  setAppDataMaxFloorLoad(
                    e.target.value ? parseFloat(e.target.value) : null,
                  );
                  setSuccessMessage("");
                }}
                id="max_floor_load"
              />
            </FormGroup>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={6}>
            <FormGroup>
              <Form.Label htmlFor="office_area">
                <strong>Kantooroppervlakte (m²)</strong>
              </Form.Label>
              <Form.Control
                size="sm"
                type="number"
                step="0.01"
                value={appDataOfficeArea !== null ? appDataOfficeArea : ""}
                onChange={(e) => {
                  setAppDataOfficeArea(
                    e.target.value ? parseFloat(e.target.value) : null,
                  );
                  setSuccessMessage("");
                }}
                id="office_area"
              />
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup>
              <Form.Label htmlFor="office_ratio">
                <strong>Kantoorratio (%)</strong>
              </Form.Label>
              <Form.Control
                disabled={true}
                readOnly={true}
                size="sm"
                type="number"
                step="1"
                value={
                  appDataOfficeArea && bagObject.floorArea
                    ? Math.round(
                        (appDataOfficeArea / bagObject.floorArea) * 100,
                      )
                    : ""
                }
                id="office_ratio"
              />
            </FormGroup>
          </Col>
        </Row>

        <div className="d-flex w-100 align-items-center justify-content-between">
          {errorMessage ? (
            <Form.Text className="text-danger mb-1">{errorMessage}</Form.Text>
          ) : (
            <div>
              {successMessage && (
                <Form.Text className="text-success mb-1">
                  {successMessage}
                </Form.Text>
              )}
            </div>
          )}
          <Button
            size="sm"
            title="Opslaan"
            type="submit"
            variant="primary"
            className="px-4"
            disabled={isLoading || !isFormComplete()}
            style={{ minWidth: "7rem" }}
          >
            {isLoading ? (
              <Spinner animation="border" variant="dark" size="sm" />
            ) : (
              "Opslaan"
            )}
          </Button>
        </div>
      </div>
    </Form>
  );
}
